<template>
  <div
    v-if="hoverRow && (selectedWidgetState === null) && !isDragging"
    class="sb-title"
  >
    <span>{{ t('statsEditor.row') }}</span>
  </div>
  <div
    class="grid stats-builder-row"
    :class="{ 'rowInHover': hoverRow && (selectedWidgetState === null) && !isDragging }"
    @mouseover="(e) => mouseoverRow(e)"
    @mouseleave="(e) => mouseleaveRow(e)"
  >
    <div
      v-if="!isDragging"
      class="sb-row-actions"
      :class="{ 'is-visible': hoverRow && (selectedWidgetState === null) }"
      @mouseover="(e) => mouseoverRow(e)"
      @mouseleave="(e) => mouseleaveRow(e)"
    >
      <div class="flex flex-column">
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.move')"
          class="flex sb-action handle mb-1"
          icon="far fa-arrows"
        >
          <i class="far fa-arrows" />
        </div>
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.editColumns')"
          class="flex sb-action mb-1"
          @click="openColumnsModal"
        >
          <i class="far fa-columns" />
        </div>
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.duplicate')"
          class="flex sb-action mb-1"
          @click="duplicateRow"
        >
          <i class="far fa-clone" />
        </div>
        <div
          v-tooltip.right="t('statsEditor.actions.tooltip.row.remove')"
          class="flex sb-action mb-1"
          @click="deleteRow"
        >
          <i class="far fa-trash" />
        </div>
      </div>
    </div>
    <StatsBuilderCol
      v-for="(columnData, ii) in rowData.children"
      :key="ii"
      :row-id="rowData.id"
      :column-data="columnData"
      :row-children-count="rowData.children.length"
      :row-has-column-with-multiple-widgets="rowHasColumnWithMultipleWidgets"
    />
  </div>
  <ConfirmDialog :group="`removeRow_${rowData.id}`" />
  <StatsBuilderAddRowModal
    v-model:visible="showColumnsModal"
    mode="edit"
    :row-data="rowData"
    @on-hide-modal="handleHideColumnsModal"
  />
</template>

<script lang="ts">
import {
  computed, defineComponent,
  Ref,
  ref, SetupContext,
  watch,
} from 'vue';
import StatsBuilderCol from '@/components/stats-builder/StatsBuilderCol.vue';
import StatsBuilderAddRowModal from '@/components/stats-builder/StatsBuilderAddRowModal.vue';
import { store } from '@/store';
import Tooltip from 'primevue/tooltip';
import { useI18n } from 'vue-i18n';
import { StatsBuilderSelectedWidget } from '@/types/stats-editor-types';
import { useConfirm } from 'primevue/useconfirm';
import ConfirmDialog from 'primevue/confirmdialog';

export default defineComponent({
  name: 'StatsBuilderRow',

  components: {
    StatsBuilderCol,
    StatsBuilderAddRowModal,
    ConfirmDialog,
  },

  directives: {
    tooltip: Tooltip,
  },

  props: {
    rowData: {
      type: Object,
      required: false,
    },

  },

  setup(props: { rowData: any }, { emit }: SetupContext) {
    const { t } = useI18n();
    const confirm = useConfirm();

    const selectedWidgetState: Ref<StatsBuilderSelectedWidget|null> = computed(() => store.getters['statsEditor/getSelectedWidget']);
    const isDragging = computed(() => store.getters['statsEditor/getIsDraggingBuilderItem']);

    const rowHasColumnWithMultipleWidgets = computed(() => {
      if (props.rowData.children) {
        return props.rowData.children.some((column: any) => {
          if (column.widgets && column.widgets.length > 1) {
            return true;
          }
          return false;
        });
      }
      return false;
    });

    const timeoutId = ref<any>(null);

    const hoverRow = ref(false);
    const hoverCol = ref(false);
    const showColumnsModal = ref(false);
    const mouseoverRow = (e: any) => {
      hoverRow.value = true;
      if (timeoutId.value) {
        clearTimeout(timeoutId.value);
      }
    };
    const mouseleaveRow = (e: any) => {
      timeoutId.value = setTimeout(() => {
        hoverRow.value = false;
      }, 200);
    };

    watch(isDragging, (dragging) => {
      if (dragging) {
        hoverRow.value = false;
      }
    });

    const openColumnsModal = () => {
      showColumnsModal.value = true;
    };

    const handleHideColumnsModal = () => {
      showColumnsModal.value = false;
    };

    const deleteRow = () => {
      confirm.require({
        message: t('statsEditor.confirmationMessages.removeRow'),
        header: 'Confirmation',
        group: `removeRow_${props.rowData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/removeRow', {
            location: {
              rowId: props.rowData.id,
            },
          });
        },
      });
    };

    const duplicateRow = () => {
      confirm.require({
        message: t('statsEditor.confirmationMessages.duplicateRow'),
        header: 'Confirmation',
        group: `removeRow_${props.rowData.id}`,
        icon: 'far fa-exclamation-triangle',
        acceptLabel: t('yes'),
        rejectLabel: t('no'),
        accept: () => {
          store.dispatch('statsEditor/duplicateRow', {
            location: {
              rowId: props.rowData.id,
            },
          });
        },
      });
    };

    return {
      t,
      selectedWidgetState,
      hoverRow,
      hoverCol,
      showColumnsModal,
      rowHasColumnWithMultipleWidgets,

      mouseoverRow,
      mouseleaveRow,
      openColumnsModal,
      handleHideColumnsModal,
      deleteRow,
      duplicateRow,
      isDragging,
    };
  },
});
</script>

<style scoped lang="scss">
  .sb-title {
    position: absolute;
    top: -1.25rem;
    left: -0.2rem;
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.45rem 0.85rem 0.4rem;
    border-radius: 999px;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.95), rgba(231, 247, 206, 0.92));
    border: 1px solid rgba(148, 200, 64, 0.45);
    color: #314911;
    font-size: 0.78rem;
    font-weight: 650;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    line-height: 1rem;
    box-shadow: 0 14px 30px -20px rgba(47, 71, 11, 0.45);
    z-index: 12;
  }

  .stats-builder-row {
    position: relative;
    border-radius: 0.75rem;
    border: 1px solid #e2e8d9;
    padding: 1rem 1.25rem 1rem 3.25rem;
    background-color: #ffffff;
    transition: border-color 0.25s ease, box-shadow 0.25s ease, transform 0.25s ease;
    align-items: stretch; // Force les colonnes à avoir la même hauteur
  }

  .stats-builder-row.rowInHover {
    z-index:11;
    border-color: #94c840;
    box-shadow: 0 20px 42px -32px rgba(53, 88, 12, 0.4), 0 0 0 1px rgba(148, 200, 64, 0.35);
    transform: translateY(-1px);
  }

  .sb-row-actions {
    position: absolute;
    top: 1.1rem;
    left: 1.1rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.18s ease, transform 0.18s ease;

    &.is-visible {
      opacity: 1;
      pointer-events: auto;
      transform: translateX(0);
    }

    .sb-action {
      cursor: pointer;
      padding: 0.55rem;
      background: #ffffff;
      border: 1px solid rgba(148, 200, 64, 0.32);
      border-radius: 0.65rem;
      box-shadow: 0 12px 24px -20px rgba(47, 71, 11, 0.35);
      transition: background 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease, color 0.2s ease;
      color: #2f3b1a;
    }

    .sb-action:hover {
      background: #ffffff;
      border-color: rgba(148, 200, 64, 0.55);
      box-shadow: 0 18px 32px -20px rgba(47, 71, 11, 0.45);
      transform: translateY(-1px);
      color: #2f3b1a;
    }
  }
</style>
